import React, { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { cn } from '../../lib/utils';
import { IconPlus, IconTrash, IconUser, IconHeart } from '../shared/icons';
import { Field, Hint } from './Field';
import { SlideList } from './SlideList';
import { CarouselList } from './CarouselList';
import { TagsEditor } from './TagsEditor';
import { BulletsEditor } from './BulletsEditor';
import { CtaEditor } from './CtaEditor';
import { ProgressEditor, StatsEditor, CalloutEditor, ImageWidgetEditor, VideoWidgetEditor } from './widgets';
import { BackgroundPicker } from './BackgroundPicker';
import { GradientEditor } from './GradientEditor';
import { GenerativeEditor } from './GenerativeEditor';
import { Primitives3DEditor } from './Primitives3DEditor';
import { FormattedTextarea } from './FormattedTextarea';
import { TemplatePicker } from './TemplatePicker';
import { PalettePicker } from './PalettePicker';
import { LogoEditor } from './LogoEditor';
import { ContentOrderEditor } from './ContentOrderEditor';
import { NormiesEditor } from './NormiesEditor';
import { ShowcaseCarouselList } from './ShowcaseCarouselList';
import { FOREST_BG_URLS, SOLID_ROWS, SOLID_BG_BY_HEX, BG_GRAIN_LEVELS, DEFAULT_BG_GRAIN, BG_COMPENSATION_LEVELS, DEFAULT_BG_COMPENSATION } from '../../constants/backgrounds';
import { colors } from '../../constants/colors';
import { DEFAULT_GENART_CONFIG } from '../../constants/genArt';
import { DEFAULT_PRIMITIVES3D } from '../../constants/primitives3d';
import { GLASS_BLUR_LEVELS, DEFAULT_GLASS_BLUR, WIDGET_OPACITY_LEVELS, DEFAULT_WIDGET_OPACITY } from '../../constants/glass';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, BODY_FONT_SIZE_LEVELS, DEFAULT_BODY_FONT_SIZE, KICKER_FONT_SIZE_LEVELS, DEFAULT_KICKER_FONT_SIZE, SUBTITLE_FONT_SIZE_LEVELS, DEFAULT_SUBTITLE_FONT_SIZE, STATS_FONT_SIZE_LEVELS, DEFAULT_STATS_FONT_SIZE, TAGS_FONT_SIZE_LEVELS, DEFAULT_TAGS_FONT_SIZE, FONT_PRESETS, DEFAULT_FONT_PRESET } from '../../constants/typography';
import { DEFAULT_BG_DARKEN, DEFAULT_BG_FX, BG_FX_OVERLAY_LEVELS, BG_FX_VIGNETTE_LEVELS, BG_FX_FOG_LEVELS, BG_FX_NOISE_LEVELS } from '../../constants/backgrounds';
import { DEFAULT_PALETTE, PALETTE_PRESETS, getPresetInfo } from '../../constants/palette';
import { DarkenSlider, ToggleSwitch, ScopeSelector, CollapseTransition, useDelayedFalse } from '../../sdk';
import { TEMPLATES } from '../../templates';

const DonationContent = lazy(() => import('../donation/DonationContent'));
const ClerkEmailCodePanel = lazy(() => import('../auth/ClerkEmailCodePanel'));
const AccountContent = lazy(() => import('../auth/AccountContent'));

/**
 * Editor panel for editing slide content
 * @param {Object} props
 * @param {import('../../types/slide').SlideData[]} props.slides - All slides
 * @param {number} props.activeIndex - Current slide index
 * @param {function} props.onSlideChange - Callback when active slide is updated
 * @param {function} props.onIndexChange - Callback when slide index changes
 * @param {function} props.onAddSlide - Callback to add new slide
 * @param {function} props.onRemoveSlide - Callback to remove current slide
 * @param {Array} props.carousels - List of all carousels
 * @param {string} props.activeCarouselId - Currently active carousel ID
 * @param {function} props.onCarouselSelect - Callback when carousel is selected
 * @param {function} props.onCarouselCreate - Callback to create new carousel
 * @param {function} props.onCarouselRename - Callback to rename carousel
 * @param {function} props.onCarouselDelete - Callback to delete carousel
 * @param {boolean} props.isLoadingCarousels - Loading state for carousels
 */
export function EditorPanel({
  slides,
  activeIndex,
  onSlideChange,
  onIndexChange,
  onAddSlide,
  onRemoveSlide,
  onDuplicateSlide,
  onReorderSlides,
  // Carousel props
  carousels = [],
  activeCarouselId = null,
  onCarouselSelect,
  onCarouselCreate,
  onCarouselRename,
  onCarouselDelete,
  isLoadingCarousels = false,
  isCreatingCarousel = false,
  // Welcome Showcase props (admin only)
  isSeed = false,
  showcaseCarousels = [],
  onShowcaseSelect,
  onShowcaseCreate,
  onShowcaseRename,
  onShowcaseDelete,
  onShowcasePublish,
  onShowcaseUnpublish,
  isCreatingShowcase = false,
  // Auth props
  user = null,
  isAuthenticated = false,
  isAnonymous = true,
  onLoginClick,
  onAccountClick,
  onDonateClick,
  showDonation,
  onDonateClose,
  showAuth,
  onAuthClose,
  onAuthSuccess,
  onSwitchToLegacyLogin,
  showAccount,
  onAccountClose,
  onBackToEditor,
  userEmail,
  // Template props
  template = 'forest-classic',
  onTemplateChange,
  onBulkSlideChange,
  // Logo props
  logoEnabled = true,
  logoUrl = null,
  logoAssetId = null,
  logoTitle = 'MAGIC FOREST',
  logoSubtitle = 'association',
  logoSize = null,
  logoIsDark = false,
  logoInvert = false,
  onLogoChange,
  // Phase 5b: asset resolve errors
  resolveErrors = {},
}) {
  const active = slides[activeIndex];
  
  // Compute effective background URL — when backgroundUrl is null, derive from slide's bgIndex
  // (matches ForestBackground which uses data.bgIndex as the variant)
  const bgVariant = typeof active?.bgIndex === 'number' ? active.bgIndex : activeIndex;
  const effectiveBgUrl = active?.backgroundUrl || 
    `${process.env.REACT_APP_BACKEND_URL || ''}${FOREST_BG_URLS[bgVariant % FOREST_BG_URLS.length]}`;

  // Background filter state (lifted from BackgroundPicker) — must be before early return
  const [bgFilter, setBgFilter] = useState('current');

  // Scope selectors for template and font pickers (persistent — stays until user clicks "This slide")
  const [templateScope, setTemplateScope] = useState('slide');
  const [fontScope, setFontScope] = useState('slide');
  const [paletteScope, setPaletteScope] = useState('slide');

  // Compute diversity counts for template and font across all slides
  const carouselTemplate = carousels?.find(c => c.id === activeCarouselId)?.template;
  const uniqueTemplates = new Set(
    slides.map(s => s.template || carouselTemplate || 'forest-classic')
  ).size;
  const uniqueFonts = new Set(
    slides.map(s => s.fontPreset ?? DEFAULT_FONT_PRESET)
  ).size;
  const uniquePalettes = new Set(
    slides.map(s => s.palette || DEFAULT_PALETTE)
  ).size;

  // ── LCD Typewriter — shared helper ──────────────────────
  // Builds a frame sequence for typing two lines, with a brief pause between them.
  const buildFrames = useCallback((l1, l2) => {
    const frames = [];
    for (let i = 1; i <= l1.length; i++)
      frames.push({ line1: l1.slice(0, i), line2: '', cursorLine: 1 });
    // pause between lines (3 frames ≈ 120 ms)
    frames.push({ line1: l1, line2: '', cursorLine: 2 });
    frames.push({ line1: l1, line2: '', cursorLine: 2 });
    frames.push({ line1: l1, line2: '', cursorLine: 2 });
    for (let i = 1; i <= l2.length; i++)
      frames.push({ line1: l1, line2: l2.slice(0, i), cursorLine: 2 });
    return frames;
  }, []);

  // ── LCD Typewriter — BOOT phase (runs on mount, before data arrives) ──
  const BOOT_L1 = 'Booting system';
  const BOOT_L2 = 'Initializing';
  const [bootText, setBootText] = useState({ line1: '', line2: '', cursorLine: 1, typing: true, done: false });

  useEffect(() => {
    const frames = buildFrames(BOOT_L1, BOOT_L2);
    let idx = -1;
    const interval = setInterval(() => {
      idx++;
      if (idx < frames.length) {
        setBootText({ ...frames[idx], typing: true, done: false });
      } else {
        clearInterval(interval);
        setBootText({ line1: BOOT_L1, line2: BOOT_L2, cursorLine: 2, typing: false, done: true });
      }
    }, 40);
    return () => clearInterval(interval);
  }, [buildFrames]); // runs once on mount

  // ── LCD Typewriter — LOADED phase (runs once when data first arrives) ──
  const LCD_LINE_1 = 'Post Builder';
  const LCD_LINE_2 = 'by Magic Forest Association';
  const [lcdReady, setLcdReady] = useState(false);
  const [lcdText, setLcdText] = useState({ line1: '', line2: '', cursorLine: 0, typing: false, done: false });

  // Flip lcdReady exactly once when active first becomes truthy
  useEffect(() => {
    if (active && !lcdReady) setLcdReady(true);
  }, [active, lcdReady]);

  // Start typing when lcdReady flips — this dep is stable (never changes again)
  useEffect(() => {
    if (!lcdReady) return;

    const frames = buildFrames(LCD_LINE_1, LCD_LINE_2);
    let idx = -1;
    setLcdText({ line1: '', line2: '', cursorLine: 1, typing: true, done: false });

    const interval = setInterval(() => {
      idx++;
      if (idx < frames.length) {
        setLcdText({ ...frames[idx], typing: true, done: false });
      } else {
        clearInterval(interval);
        setLcdText({ line1: LCD_LINE_1, line2: LCD_LINE_2, cursorLine: 0, typing: false, done: true });
      }
    }, 40);

    return () => clearInterval(interval);
  }, [lcdReady, buildFrames]); // lcdReady flips once, buildFrames is stable — interval never interrupted

  // ── Delayed tile-class hooks (MUST be before early return) ──
  // When active is null (loading), all flags default to false.
  const _solidOn    = !!active?.bgSolid;
  const _gradientOn = !!active?.gradient?.enabled;
  const _genOn      = !!active?.generative?.enabled;
  const _primOn     = !!active?.primitives3d?.enabled;
  const _kickerOn   = active ? (active.kickerEnabled !== false && (active.kickerEnabled === true || !!active.kicker)) : false;
  const _subtitleOn = active ? (active.subtitleEnabled !== false && (active.subtitleEnabled === true || !!active.subtitle)) : false;

  // Blocks-group enabled derivations (safe when active is null)
  const _bulletsOn  = active ? (active.bulletsEnabled !== false && (active.bulletsEnabled === true || !!(active.bullets?.length))) : false;
  const _tagsOn     = active ? (active.tagsEnabled !== false) : false;
  const _progressOn = !!active?.progress;
  const _statsOn    = !!(active?.stats && active?.stats.length > 0);
  const _calloutOn  = !!active?.callout;
  const _imageOn    = !!active?.imageWidget;
  const _videoOn    = !!active?.videoWidget;
  const _logoOn     = logoEnabled;
  const _footerOn   = active ? (active.footerEnabled !== false) : false;

  const delayedSolidExpanded    = useDelayedFalse(_solidOn,    230);
  const delayedGradientExpanded = useDelayedFalse(_gradientOn, 230);
  const delayedGenExpanded      = useDelayedFalse(_genOn,      230);
  const delayedPrimExpanded     = useDelayedFalse(_primOn,     230);
  const delayedKickerExpanded   = useDelayedFalse(_kickerOn,   230);
  const delayedSubtitleExpanded = useDelayedFalse(_subtitleOn, 230);

  // Blocks-group delayed values (tile width stays expanded during collapse anim)
  const delayedBulletsExpanded  = useDelayedFalse(_bulletsOn,  230);
  const delayedTagsExpanded     = useDelayedFalse(_tagsOn,     230);
  const delayedProgressExpanded = useDelayedFalse(_progressOn, 230);
  const delayedStatsExpanded    = useDelayedFalse(_statsOn,    230);
  const delayedCalloutExpanded  = useDelayedFalse(_calloutOn,  230);
  const delayedImageExpanded    = useDelayedFalse(_imageOn,    230);
  const delayedVideoExpanded    = useDelayedFalse(_videoOn,    230);
  const delayedLogoExpanded     = useDelayedFalse(_logoOn,     230);
  const delayedFooterExpanded   = useDelayedFalse(_footerOn,   230);

  // Early return if no active slide (during loading) — skeleton UI
  if (!active) {
    return (
      <div className="editor-panel max-h-full h-full overflow-y-auto scrollbar-thin flex flex-col">
        {/* Header — LCD screen (loading state) */}
        <div className="flex items-center gap-2 mb-3">
          <div className="header-lcd-bezel" style={{ maxWidth: 'calc(100% - 160px)' }}>
            <div className="header-lcd-led header-lcd-led-loading" />
            <div className="header-lcd-inner">
              <div className="header-lcd-title">QooQoo 🦜</div>
              <div className="header-lcd-sep" />
              <div className="header-lcd-info">
                <div className="header-lcd-info-line header-lcd-info-loading">
                  {bootText.line1}
                  {bootText.cursorLine === 1 && <span className="header-lcd-cursor">_</span>}
                </div>
                <div className="header-lcd-info-line header-lcd-info-loading">
                  {bootText.line2}
                  {bootText.cursorLine === 2 && <span className="header-lcd-cursor">_</span>}
                </div>
              </div>
            </div>
          </div>
          <div className="btn-instrument-group flex-shrink-0">
            {showDonation ? (
              <button onClick={onBackToEditor} className="btn-instrument btn-instrument-sm" title="Home">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                </svg>
                Home
              </button>
            ) : (
              <button onClick={onDonateClick} className="btn-instrument btn-instrument-sm btn-instrument-pink" title="Donate">
                <IconHeart className="h-3 w-3" />
                Donate
              </button>
            )}
            {(showAuth || showAccount) ? (
              <button onClick={onBackToEditor} className="btn-instrument btn-instrument-sm" title="Home">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                </svg>
                Home
              </button>
            ) : (
              <button onClick={onLoginClick} className="btn-instrument btn-instrument-sm" title="Log In">
                <IconUser className="h-3 w-3" />
                Log In
              </button>
            )}
          </div>
        </div>

        {/* Skeleton: MEDIA — Carousels + Slides */}
        <div className="field-container">
          <div className="flex items-center justify-between mb-1.5">
            <div className="tpl-zone-header">Carousels</div>
            <div className="flex gap-1">
              {[0,1,2,3,4].map(i => (
                <div key={i} className="h-[16px] rounded skeleton-pulse" style={{ width: i === 0 ? '40px' : '32px', animationDelay: `${i * 0.03}s` }} />
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-1">
            <div className="h-[30px] w-[130px] rounded-md skeleton-pulse" />
            <div className="h-[30px] w-[60px] rounded-md skeleton-pulse" style={{ animationDelay: '0.06s', opacity: 0.4 }} />
          </div>
          <div className="h-[5px] w-28 rounded skeleton-pulse mb-3" style={{ animationDelay: '0.1s', opacity: 0.3 }} />

          <div className="tpl-zone-header mb-1.5">Slides</div>
          <div style={{ background: '#101112', borderRadius: '8px', padding: '8px', border: '1px solid rgba(255,255,255,0.03)' }}>
            <div className="flex flex-wrap gap-2">
              {[0,1,2,3,4,5].map(i => (
                <div key={i} className="h-[30px] rounded-[6px] skeleton-pulse" style={{ width: '48px', animationDelay: `${i * 0.04}s` }} />
              ))}
              <div className="h-[30px] rounded-[6px] skeleton-pulse" style={{ width: '52px', animationDelay: '0.3s', opacity: 0.3 }} />
            </div>
          </div>
        </div>

        {/* Skeleton: remaining sections */}
        <div className="space-y-4 mt-5">

          {/* Skeleton: BG — Solid + Gradient tile pair */}
          <div className="block-grid">
            <div className="block-tile">
              <div className="field-container">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-[11px] w-32 rounded skeleton-pulse" />
                    <div className="h-[5px] w-40 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.02s' }} />
                  </div>
                  <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '0.04s' }} />
                </div>
              </div>
            </div>
            <div className="block-tile">
              <div className="field-container">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-[11px] w-28 rounded skeleton-pulse" style={{ animationDelay: '0.06s' }} />
                    <div className="h-[5px] w-36 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.08s' }} />
                  </div>
                  <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '0.1s' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Skeleton: BG — Generative + 3D Primitives tile pair */}
          <div className="block-grid">
            <div className="block-tile">
              <div className="field-container">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-[11px] w-36 rounded skeleton-pulse" style={{ animationDelay: '0.12s' }} />
                    <div className="h-[5px] w-44 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.14s' }} />
                  </div>
                  <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '0.16s' }} />
                </div>
              </div>
            </div>
            <div className="block-tile">
              <div className="field-container">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-[11px] w-28 rounded skeleton-pulse" style={{ animationDelay: '0.18s' }} />
                    <div className="h-[5px] w-36 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.2s' }} />
                  </div>
                  <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '0.22s' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Skeleton: BG — Background picker */}
          <div className="field-container">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="h-[11px] w-20 rounded skeleton-pulse" style={{ animationDelay: '0.24s' }} />
                <div className="h-[5px] w-40 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.26s' }} />
              </div>
            </div>
            <div className="h-[100px] w-full rounded-lg skeleton-pulse" style={{ animationDelay: '0.28s' }} />
            <div className="flex gap-2 mt-3">
              {[0,1,2,3].map(i => (
                <div key={i} className="h-12 w-16 rounded-md skeleton-pulse" style={{ animationDelay: `${0.3 + i * 0.03}s` }} />
              ))}
            </div>
          </div>

          {/* Skeleton: LAYOUT — Template */}
          <div className="field-container">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="h-[11px] w-20 rounded skeleton-pulse" style={{ animationDelay: '0.34s' }} />
                <div className="h-[5px] w-36 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.36s' }} />
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="h-[10px] w-14 rounded skeleton-pulse" style={{ animationDelay: '0.38s' }} />
                <div className="h-[10px] w-14 rounded skeleton-pulse" style={{ animationDelay: '0.4s', opacity: 0.4 }} />
              </div>
            </div>
            <div className="module-rule" />

            <div className="tpl-zone-header mb-2">Pinned</div>
            <div className="grid grid-cols-4 gap-1.5">
              {[0,1,2].map(i => (
                <div key={i} className="tpl-btn" style={{ pointerEvents: 'none' }}>
                  <div className="w-full aspect-[4/5] rounded-[3px] skeleton-pulse" style={{ animationDelay: `${0.42 + i * 0.06}s` }} />
                  <div className="h-[7px] w-3/4 rounded skeleton-pulse mt-1" style={{ animationDelay: `${0.48 + i * 0.06}s` }} />
                </div>
              ))}
              <div className="tpl-import" style={{ pointerEvents: 'none' }}>
                <div className="w-full aspect-[4/5] flex items-center justify-center" style={{ color: 'rgba(255,255,255,0.08)' }}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </div>
                <div className="h-[7px] w-8 rounded skeleton-pulse mt-1 mx-auto" style={{ animationDelay: '0.6s', opacity: 0.3 }} />
              </div>
            </div>

            <div className="tpl-zone-header mt-4 mb-2">Preset Library</div>
            <div className="flex gap-1 overflow-hidden pb-1">
              <div className="h-6 w-6 rounded skeleton-pulse flex-shrink-0" style={{ animationDelay: '0.62s' }} />
              {[0,1,2,3,4,5].map(i => (
                <div key={i} className="h-6 rounded skeleton-pulse flex-shrink-0" style={{ width: i < 2 ? '52px' : '36px', animationDelay: `${0.64 + i * 0.03}s` }} />
              ))}
            </div>
          </div>

          {/* Skeleton: FX — Glass Effect */}
          <div className="field-container">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="h-[11px] w-20 rounded skeleton-pulse" style={{ animationDelay: '0.7s' }} />
                <div className="h-[5px] w-40 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.72s' }} />
              </div>
            </div>
            <div className="h-5 w-full rounded skeleton-pulse" style={{ animationDelay: '0.74s' }} />
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="h-5 w-full rounded skeleton-pulse" style={{ animationDelay: '0.76s' }} />
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="h-5 w-full rounded skeleton-pulse" style={{ animationDelay: '0.78s' }} />
            </div>
          </div>

          {/* Skeleton: TYPE — Font Preset */}
          <div className="field-container">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="h-[11px] w-24 rounded skeleton-pulse" style={{ animationDelay: '0.8s' }} />
                <div className="h-[5px] w-40 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.82s' }} />
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="h-[10px] w-14 rounded skeleton-pulse" style={{ animationDelay: '0.84s' }} />
                <div className="h-[10px] w-14 rounded skeleton-pulse" style={{ animationDelay: '0.86s', opacity: 0.4 }} />
              </div>
            </div>
            <div className="module-rule" />
            <div className="grid grid-cols-3 gap-2">
              {[0,1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="h-[52px] rounded-lg skeleton-pulse" style={{ animationDelay: `${0.88 + i * 0.03}s` }} />
              ))}
            </div>
          </div>

          {/* Skeleton: HEADER — Title */}
          <div className="field-container">
            <div className="h-[11px] w-12 rounded skeleton-pulse" style={{ animationDelay: '0.92s' }} />
            <div className="module-rule" />
            <div className="h-[52px] w-full rounded-lg skeleton-pulse" style={{ animationDelay: '0.94s' }} />
          </div>

          {/* Skeleton: TEXT — Kicker + Subtitle tile pair */}
          <div className="block-grid">
            <div className="block-tile">
              <div className="field-container">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-[11px] w-14 rounded skeleton-pulse" style={{ animationDelay: '0.96s' }} />
                    <div className="h-[5px] w-28 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '0.98s' }} />
                  </div>
                  <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '1.0s' }} />
                </div>
              </div>
            </div>
            <div className="block-tile">
              <div className="field-container">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-[11px] w-16 rounded skeleton-pulse" style={{ animationDelay: '1.02s' }} />
                    <div className="h-[5px] w-24 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '1.04s' }} />
                  </div>
                  <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '1.06s' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Skeleton: TEXT — Body */}
          <div className="field-container">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-[11px] w-10 rounded skeleton-pulse" style={{ animationDelay: '1.08s' }} />
                <div className="h-[5px] w-24 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '1.1s' }} />
              </div>
              <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '1.12s' }} />
            </div>
          </div>

          {/* Skeleton: COLOR — Color Palette */}
          <div className="field-container">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="h-[11px] w-24 rounded skeleton-pulse" style={{ animationDelay: '1.14s' }} />
                <div className="h-[5px] w-40 rounded skeleton-pulse mt-1.5" style={{ animationDelay: '1.16s' }} />
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="h-[10px] w-14 rounded skeleton-pulse" style={{ animationDelay: '1.18s' }} />
                <div className="h-[10px] w-14 rounded skeleton-pulse" style={{ animationDelay: '1.2s', opacity: 0.4 }} />
              </div>
            </div>
            <div className="module-rule" />
            <div className="flex flex-wrap gap-1.5">
              {[0,1,2,3,4,5,6,7,8,9].map(i => (
                <div key={i} className="h-7 w-7 rounded-lg skeleton-pulse" style={{ animationDelay: `${1.22 + i * 0.02}s` }} />
              ))}
            </div>
          </div>

          {/* Skeleton: BLOCKS — Widget tiles */}
          <div className="block-grid">
            {['Bullets','Tags','Progress','Stats','Callout','Image','Video'].map((label, i) => (
              <div key={label} className="block-tile">
                <div className="field-container">
                  <div className="flex items-center justify-between">
                    <div className="h-[11px] rounded skeleton-pulse" style={{ width: `${40 + (i % 3) * 12}px`, animationDelay: `${1.3 + i * 0.03}s` }} />
                    <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: `${1.32 + i * 0.03}s` }} />
                  </div>
                </div>
              </div>
            ))}
            {/* Logo tile */}
            <div className="block-tile">
              <div className="field-container">
                <div className="flex items-center justify-between">
                  <div className="h-[11px] w-12 rounded skeleton-pulse" style={{ animationDelay: '1.55s' }} />
                  <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '1.57s' }} />
                </div>
              </div>
            </div>
            {/* Footer tile */}
            <div className="block-tile">
              <div className="field-container">
                <div className="flex items-center justify-between">
                  <div className="h-[11px] w-14 rounded skeleton-pulse" style={{ animationDelay: '1.6s' }} />
                  <div className="h-5 w-9 rounded-full skeleton-pulse" style={{ animationDelay: '1.62s' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const updateActive = (patch) => {
    const updated = { ...active, ...patch };
    // Ensure CTA has a default if neither cta nor ctaButton is set
    if (!updated.cta && !updated.ctaButton) {
      updated.cta = `Slide ${activeIndex + 1} / ${slides.length}`;
    }
    onSlideChange(updated);
  };

  // Kicker, Subtitle, Body, and Bullets enabled states are stored in slide data
  const kickerEnabled = active.kickerEnabled !== false && (active.kickerEnabled === true || !!active.kicker);
  const subtitleEnabled = active.subtitleEnabled !== false && (active.subtitleEnabled === true || !!active.subtitle);
  const bodyEnabled = active.bodyEnabled !== false && (active.bodyEnabled === true || !!active.body);
  const bulletsEnabled = active.bulletsEnabled !== false && (active.bulletsEnabled === true || !!(active.bullets?.length));
  const footerEnabled = active.footerEnabled !== false;

  // Background-pair enabled states (hoisted from IIFEs for delayed tile layout)
  const solidEnabled = _solidOn;
  const gradientEnabled = _gradientOn;
  const genEnabled = _genOn;
  const primEnabled = _primOn;

  // Tile classes computed from DELAYED values (width stays expanded during collapse anim)
  const delayedBothOff    = !delayedSolidExpanded && !delayedGradientExpanded;
  const delayedBothGenOff = !delayedGenExpanded   && !delayedPrimExpanded;

  /* ── Smart tile layout: prevent orphan half-width tiles ── */
  const computeTileCls = (states) => {
    const cls = {};
    let run = [];
    const flushRun = () => {
      if (run.length % 2 === 1) cls[run[run.length - 1].key] = 'block-expanded';
      run = [];
    };
    for (const b of states) {
      if (b.on) { flushRun(); cls[b.key] = 'block-expanded'; }
      else      { run.push(b); cls[b.key] = 'block-tile'; }
    }
    flushRun();
    return cls;
  };

  const kickerSubTileCls = computeTileCls([
    { key: 'kicker',   on: delayedKickerExpanded },
    { key: 'subtitle', on: delayedSubtitleExpanded },
  ]);

  const tileCls = computeTileCls([
    { key: 'bullets',      on: delayedBulletsExpanded },
    { key: 'tags',         on: delayedTagsExpanded },
    { key: 'progress',     on: delayedProgressExpanded },
    { key: 'stats',        on: delayedStatsExpanded },
    { key: 'callout',      on: delayedCalloutExpanded },
    { key: 'imageWidget',  on: delayedImageExpanded },
    { key: 'videoWidget',  on: delayedVideoExpanded },
    { key: 'logo',         on: delayedLogoExpanded },
    { key: 'footer',       on: delayedFooterExpanded },
  ]);

  return (
    <div id="editor-scroll-container" className="editor-panel max-h-full h-full overflow-y-auto scrollbar-thin">
      {/* Header — LCD Screen */}
      <div className="flex items-center gap-2 mb-3">
        <div className="header-lcd-bezel" style={{ maxWidth: 'calc(100% - 160px)' }}>
          <div className={`header-lcd-led${lcdText.typing ? ' header-lcd-led-loading' : ''}`} />
          <div className="header-lcd-inner">
            <div className="header-lcd-title">QooQoo 🦜</div>
            <div className="header-lcd-sep" />
            <div className="header-lcd-info">
              <div className="header-lcd-info-line header-lcd-info-line-bright">
                {lcdText.done ? LCD_LINE_1 : lcdText.line1}
                {lcdText.typing && lcdText.cursorLine === 1 && <span className="header-lcd-cursor">_</span>}
              </div>
              <div className="header-lcd-info-line">
                {lcdText.done ? (
                  <a href="https://magicforest.land" target="_blank" rel="noopener noreferrer" className="header-lcd-link">{LCD_LINE_2}</a>
                ) : (
                  <>{lcdText.line2}</>
                )}
                {lcdText.typing && lcdText.cursorLine === 2 && <span className="header-lcd-cursor">_</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="btn-instrument-group flex-shrink-0">
          {/* Donate / Home swap */}
          {showDonation ? (
            <button onClick={onBackToEditor} className="btn-instrument btn-instrument-sm" title="Home">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
              </svg>
              Home
            </button>
          ) : (
            <button onClick={onDonateClick} className="btn-instrument btn-instrument-sm btn-instrument-pink" title="Donate">
              <IconHeart className="h-3 w-3" />
              Donate
            </button>
          )}
          {/* User/Login / Home swap */}
          {(showAuth || showAccount) ? (
            <button onClick={onBackToEditor} className="btn-instrument btn-instrument-sm" title="Home">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
              </svg>
              Home
            </button>
          ) : isAuthenticated ? (
            <button
              onClick={onAccountClick}
              className="btn-instrument btn-instrument-sm btn-instrument-green"
              title="Account"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span className="max-w-[72px] truncate">{user?.name || 'User'}</span>
            </button>
          ) : (
            <button
              onClick={onLoginClick}
              className="btn-instrument btn-instrument-sm"
              title="Log In"
            >
              <IconUser className="h-3 w-3" />
              Log In
            </button>
          )}
        </div>
      </div>

      {/* ── Donation mode: renders inside EditorPanel, LCD header stays visible ── */}
      {showDonation ? (
        <Suspense fallback={<div className="field-container"><div className="text-white/40 text-xs text-center py-6">Loading donation flow…</div></div>}>
          <DonationContent onBack={onDonateClose} userEmail={userEmail} />
        </Suspense>
      ) : showAuth ? (
        <Suspense fallback={<div className="field-container"><div className="text-white/40 text-xs text-center py-6">Loading…</div></div>}>
          <ClerkEmailCodePanel onBack={onAuthClose} onSwitchToLegacy={onSwitchToLegacyLogin} onSuccess={onAuthSuccess} />
        </Suspense>
      ) : showAccount ? (
        <Suspense fallback={<div className="field-container"><div className="text-white/40 text-xs text-center py-6">Loading…</div></div>}>
          <AccountContent onBack={onAccountClose} />
        </Suspense>
      ) : (
      <>
      {/* ═══ MEDIA section — Carousel + Slide tabs ═══ */}
      <div id="section-media" className="field-container">
          {/* Carousel tabs */}
          {onCarouselSelect && (
            <CarouselList
              carousels={carousels}
              activeId={activeCarouselId}
              onSelect={onCarouselSelect}
              onCreate={onCarouselCreate}
              onRename={onCarouselRename}
              onDelete={onCarouselDelete}
              isLoading={isLoadingCarousels}
              isCreating={isCreatingCarousel}
            />
          )}

          {/* Slide cards */}
          <SlideList
            slides={slides}
            activeIndex={activeIndex}
            onSelect={onIndexChange}
            onReorder={onReorderSlides}
            onAdd={onAddSlide}
            onRemove={onRemoveSlide}
            onDuplicate={onDuplicateSlide}
          />
      </div>

      {/* Section content — shows only the active section */}
      <div className="mt-5 space-y-4">

        {/* ═══ SHOWCASE section — Welcome Showcase (admin only) ═══ */}
        {isSeed && (
          <div id="section-showcase" className="field-container">
            <ShowcaseCarouselList
              showcases={showcaseCarousels}
              activeId={activeCarouselId}
              onSelect={onShowcaseSelect}
              onCreate={onShowcaseCreate}
              onRename={onShowcaseRename}
              onDelete={onShowcaseDelete}
              onPublish={onShowcasePublish}
              onUnpublish={onShowcaseUnpublish}
              isCreating={isCreatingShowcase}
            />
          </div>
        )}

        {/* ═══ NORMIES section — Normies Studio ═══ */}
        <div id="section-normies">
        <NormiesEditor
          normies={active.normies}
          slide={active}
          onChange={(patch, isFullSlidePatch) => {
            if (isFullSlidePatch) {
              // Full slide patch from Apply — merge everything
              updateActive(patch);
            } else {
              // Partial normies config update (toggle, mode change)
              updateActive({ normies: patch });
            }
          }}
        />
        </div>

        {/* ═══ BG section — Static backgrounds ═══ */}
        <div id="section-bg" className="space-y-4">
        {/* Solid + Gradient — side-by-side tiles when both inactive */}
        {(() => {
            return (
              <div className="block-grid">
              <div className={delayedBothOff ? 'block-tile' : 'block-expanded'}>
        {/* Solid Color Background — toggle + auto-select + recommended highlights */}
        <div id="section-bg-solid" className="field-container">
          {(() => {
            const paletteInfo = getPresetInfo(active.palette || DEFAULT_PALETTE);
            const paletteIsDark = paletteInfo ? paletteInfo.isDark : true;

            // Auto-select: pick the best neutral color when toggling ON
            const autoSelectHex = paletteIsDark ? colors.slate[900] : colors.slate[50];

            const handleSolidPick = (hex) => {
              const patch = { bgSolid: hex };
              if (active.gradient?.enabled) {
                patch.gradient = { ...active.gradient, enabled: false };
              }
              if (active.generative?.enabled) {
                patch.generative = { ...DEFAULT_GENART_CONFIG, ...active.generative, enabled: false };
              }
              if (active.primitives3d?.enabled) {
                patch.primitives3d = { ...DEFAULT_PRIMITIVES3D, ...active.primitives3d, enabled: false };
              }
              updateActive(patch);
            };
            const clearSolid = () => updateActive({ bgSolid: null, bgGrain: 0, bgCompensation: 0 });
            const activeMeta = active.bgSolid ? SOLID_BG_BY_HEX[active.bgSolid] : null;
            const grainLabel = BG_GRAIN_LEVELS[active.bgGrain ?? 0]?.label || 'None';
            const compLabel = BG_COMPENSATION_LEVELS[active.bgCompensation ?? 0]?.label || 'None';

            return (
              <>
                {/* Header + Toggle */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <div className="module-header">Solid Color Background</div>
                      {solidEnabled && (
                        <>
                          <span
                            className="h-3 w-3 rounded-full ring-1 ring-white/20"
                            style={{ backgroundColor: active.bgSolid }}
                          />
                          <span className="text-[10px] text-white/35">
                            {activeMeta ? `${activeMeta.family} ${activeMeta.shade}` : active.bgSolid}
                            {(active.bgGrain ?? 0) > 0 && ` · grain ${grainLabel.toLowerCase()}`}
                            {(active.bgCompensation ?? 0) > 0 && ` · comp ${compLabel}`}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="mt-1 module-desc">Use a flat color instead of an image.</div>
                  </div>
                  <ToggleSwitch
                    checked={solidEnabled}
                    onChange={(v) => {
                      if (v) {
                        handleSolidPick(autoSelectHex);
                      } else {
                        clearSolid();
                      }
                    }}
                  />
                </div>

                {/* Color swatches + controls — only when enabled */}
                <CollapseTransition open={solidEnabled}>
                  <>
                    <div className="space-y-1">
                      {SOLID_ROWS.map((row, ri) => {
                        return (
                          <div key={ri} className="flex flex-wrap gap-1">
                            {row.map((c) => {
                              const isActive = active.bgSolid === c.hex;
                              const isVeryLight = c.shade <= 100 && (c.family === 'gray' || c.family === 'neutral' || c.family === 'zinc');
                              const isBlack = c.shade >= 900 && (c.family === 'gray' || c.family === 'neutral' || c.family === 'zinc');
                              const needsRing = isVeryLight || isBlack;
                              return (
                                <button
                                  key={c.id}
                                  type="button"
                                  title={c.name}
                                  onClick={() => handleSolidPick(c.hex)}
                                  className={`h-7 w-7 rounded-lg border transition-colors flex items-center justify-center ${
                                    isActive
                                      ? 'border-emerald-400 bg-emerald-400/15'
                                      : 'border-white/10 bg-white/5 hover:bg-white/15'
                                  }`}
                                >
                                  <span
                                    className={`h-3 w-3 rounded-full${needsRing ? ' ring-1 ring-white/30' : ''}`}
                                    style={{ backgroundColor: c.hex }}
                                  />
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>

                    {/* Grain + Compensation sliders */}
                    <div className="mt-3 space-y-2">
                      <DarkenSlider
                        label="Grain"
                        levels={BG_GRAIN_LEVELS}
                        value={active.bgGrain ?? DEFAULT_BG_GRAIN}
                        onChange={(v) => updateActive({ bgGrain: v })}
                      />
                      <DarkenSlider
                        label="Compensation"
                        levels={BG_COMPENSATION_LEVELS}
                        value={active.bgCompensation ?? DEFAULT_BG_COMPENSATION}
                        onChange={(v) => updateActive({ bgCompensation: v })}
                      />
                    </div>
                  </>
                </CollapseTransition>
              </>
            );
          })()}
        </div>
              </div>

              <div id="section-bg-gradient" className={delayedBothOff ? 'block-tile' : 'block-expanded'}>
        {/* Gradient Background — mutually exclusive with Solid + Generative + 3D Primitives */}
        <GradientEditor
          gradient={active.gradient}
          onChange={(g) => {
            const patch = { gradient: g };
            if (g.enabled) {
              patch.bgSolid = null;
              if (active.generative?.enabled) {
                patch.generative = { ...DEFAULT_GENART_CONFIG, ...active.generative, enabled: false };
              }
              if (active.primitives3d?.enabled) {
                patch.primitives3d = { ...DEFAULT_PRIMITIVES3D, ...active.primitives3d, enabled: false };
              }
            }
            updateActive(patch);
          }}
        />
              </div>
              </div>
            );
        })()}

        {/* Generative + 3D Primitives — side-by-side tiles when both inactive */}
        {(() => {
            return (
              <div id="section-genart" className="block-grid">
              <div className={delayedBothGenOff ? 'block-tile' : 'block-expanded'}>
        <GenerativeEditor
          generative={active.generative}
          onChange={(gen) => {
            const patch = { generative: gen };
            if (gen.enabled) {
              patch.bgSolid = null;
              if (active.gradient?.enabled) {
                patch.gradient = { ...active.gradient, enabled: false };
              }
              if (active.primitives3d?.enabled) {
                patch.primitives3d = { ...DEFAULT_PRIMITIVES3D, ...active.primitives3d, enabled: false };
              }
            }
            updateActive(patch);
          }}
          slides={slides}
          activeIndex={activeIndex}
          onBulkSlideChange={onBulkSlideChange}
        />
              </div>
              <div id="section-bg-primitives" className={delayedBothGenOff ? 'block-tile' : 'block-expanded'}>
        <Primitives3DEditor
          primitives3d={active.primitives3d}
          onChange={(c) => {
            const patch = { primitives3d: c };
            if (c.enabled) {
              patch.bgSolid = null;
              if (active.gradient?.enabled) {
                patch.gradient = { ...active.gradient, enabled: false };
              }
              if (active.generative?.enabled) {
                patch.generative = { ...DEFAULT_GENART_CONFIG, ...active.generative, enabled: false };
              }
            }
            updateActive(patch);
          }}
        />
              </div>
              </div>
            );
        })()}

        {/* Background */}
        <div id="section-bg-image" className="field-container">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="module-header">Background</div>
              <div className="module-desc">Select or upload slide background.</div>
            </div>
          </div>

          <BackgroundPicker
            currentBgUrl={effectiveBgUrl}
            currentBgAssetId={active?.bgAssetId || null}
            onSelect={({ url, asset_id }) => {
              const patch = { backgroundUrl: url, bgAssetId: asset_id || null, bgSolid: null, bgGrain: 0, bgCompensation: 0 };
              // Turn off generators — image selection is a clear signal
              if (active.gradient?.enabled) {
                patch.gradient = { ...active.gradient, enabled: false };
              }
              if (active.generative?.enabled) {
                patch.generative = { ...DEFAULT_GENART_CONFIG, ...active.generative, enabled: false };
              }
              if (active.primitives3d?.enabled) {
                patch.primitives3d = { ...DEFAULT_PRIMITIVES3D, ...active.primitives3d, enabled: false };
              }
              updateActive(patch);
            }}
            slides={slides}
            activeCarouselId={activeCarouselId}
            filter={bgFilter}
            onFilterChange={setBgFilter}
          />

          {/* Background Darken + Visual Effects — only for image backgrounds (solid/gradient/generative/primitives3d are independent) */}
          {!active.bgSolid && !(active.gradient && active.gradient.enabled) && !(active.generative && active.generative.enabled) && !(active.primitives3d && active.primitives3d.enabled) && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="mb-3 module-desc">Darken the background to improve text readability.</div>
            <DarkenSlider
              value={active.bgDarken ?? DEFAULT_BG_DARKEN}
              onChange={(v) => updateActive({ bgDarken: v })}
            />

            {/* ── Visual Effects (Overlay, Vignette, Fog, Noise) ── */}
            <div className="mt-5 pt-4 border-t border-white/[0.06]">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] text-white/55 font-medium tracking-wide uppercase">Visual Effects</div>
                <ToggleSwitch
                  checked={(active.bgFx ?? DEFAULT_BG_FX).enabled !== false}
                  onChange={(on) => {
                    const current = active.bgFx ?? DEFAULT_BG_FX;
                    updateActive({ bgFx: { ...current, enabled: on } });
                  }}
                  size="sm"
                />
              </div>
              <CollapseTransition open={(active.bgFx ?? DEFAULT_BG_FX).enabled !== false}>
                <div className="space-y-3">
                  <DarkenSlider
                    label="Overlay"
                    levels={BG_FX_OVERLAY_LEVELS}
                    value={(active.bgFx ?? DEFAULT_BG_FX).overlay ?? DEFAULT_BG_FX.overlay}
                    onChange={(v) => {
                      const current = active.bgFx ?? DEFAULT_BG_FX;
                      updateActive({ bgFx: { ...current, overlay: v } });
                    }}
                  />
                  <DarkenSlider
                    label="Vignette"
                    levels={BG_FX_VIGNETTE_LEVELS}
                    value={(active.bgFx ?? DEFAULT_BG_FX).vignette ?? DEFAULT_BG_FX.vignette}
                    onChange={(v) => {
                      const current = active.bgFx ?? DEFAULT_BG_FX;
                      updateActive({ bgFx: { ...current, vignette: v } });
                    }}
                  />
                  <DarkenSlider
                    label="Fog"
                    levels={BG_FX_FOG_LEVELS}
                    value={(active.bgFx ?? DEFAULT_BG_FX).fog ?? DEFAULT_BG_FX.fog}
                    onChange={(v) => {
                      const current = active.bgFx ?? DEFAULT_BG_FX;
                      updateActive({ bgFx: { ...current, fog: v } });
                    }}
                  />
                  <DarkenSlider
                    label="Noise"
                    levels={BG_FX_NOISE_LEVELS}
                    value={(active.bgFx ?? DEFAULT_BG_FX).noise ?? DEFAULT_BG_FX.noise}
                    onChange={(v) => {
                      const current = active.bgFx ?? DEFAULT_BG_FX;
                      updateActive({ bgFx: { ...current, noise: v } });
                    }}
                  />
                </div>
              </CollapseTransition>
            </div>
          </div>
          )}

        </div>
        </div>

        {/* ═══ LAYOUT section — Template picker (Session 38: Instrument-style) ═══ */}
        <div id="section-layout" className="scroll-mt-2">
            <div className="field-container">
              {/* Module header row — title left, LED scope right */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-shrink-0">
                  <div className="module-header">Template</div>
                  <div className="module-desc">Choose a layout style for slides.</div>
                </div>
                <ScopeSelector
                  variant="led"
                  scope={templateScope}
                  onScopeChange={setTemplateScope}
                  uniqueCount={uniqueTemplates}
                  diversityNoun="templates"
                  confirmMessage={`Slides use ${uniqueTemplates} different templates. Apply "${TEMPLATES[template]?.name || template}" to all ${slides.length} slides?`}
                  onConfirmApply={() => {
                    const name = TEMPLATES[template]?.name || template;
                    onBulkSlideChange({ template }, `Template changed to ${name} on all slides`);
                    onTemplateChange(template);
                  }}
                />
              </div>
              <div className="module-rule" />
              <TemplatePicker
                value={template}
                slides={slides}
                defaultTemplate={carouselTemplate || 'forest-classic'}
                onChange={(id) => {
                  if (templateScope === 'all') {
                    const name = TEMPLATES[id]?.name || id;
                    onBulkSlideChange({ template: id }, `Template changed to ${name} on all slides`);
                    onTemplateChange(id);
                  } else {
                    updateActive({ template: id });
                  }
                }}
              />
            </div>
        </div>

        {/* ═══ FX section — Glass Effect ═══ */}
        <div id="section-fx" className="space-y-4">

        {/* Glass Effect */}
        <div className="field-container">
          <div className="mb-3 flex items-start justify-between">
            <div>
              <div className="module-header">Glass Effect</div>
              <div className="module-desc">Adjust the blur intensity of the card.</div>
            </div>
            <button
              type="button"
              title="Reset to defaults"
              onClick={() => updateActive({ glassBlur: DEFAULT_GLASS_BLUR, widgetBlur: DEFAULT_GLASS_BLUR, widgetOpacity: DEFAULT_WIDGET_OPACITY, widgetBorder: true })}
              className="mt-0.5 p-1 rounded-md text-white/30 hover:text-white/70 hover:bg-white/10 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
              </svg>
            </button>
          </div>
          <DarkenSlider
            levels={GLASS_BLUR_LEVELS}
            value={active.glassBlur ?? DEFAULT_GLASS_BLUR}
            onChange={(v) => updateActive({ glassBlur: v })}
          />

          {/* Widget Blur Slider */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="mb-3 module-desc">Adjust the blur intensity of the widgets.</div>
            <DarkenSlider
              levels={GLASS_BLUR_LEVELS}
              value={active.widgetBlur ?? DEFAULT_GLASS_BLUR}
              onChange={(v) => updateActive({ widgetBlur: v })}
            />
          </div>

          {/* Widget Opacity Slider */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="mb-3 module-desc">Adjust the background transparency of the widgets.</div>
            <DarkenSlider
              levels={WIDGET_OPACITY_LEVELS}
              value={active.widgetOpacity ?? DEFAULT_WIDGET_OPACITY}
              onChange={(v) => updateActive({ widgetOpacity: v })}
            />
          </div>

          {/* Widget Border Toggle */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-[11px] text-white/55">Show widget borders</span>
              <ToggleSwitch
                checked={active.widgetBorder !== false}
                onChange={(v) => updateActive({ widgetBorder: v })}
              />
            </label>
          </div>
        </div>
        </div>

        {/* ═══ TYPE section — Font Preset (Session 37: Instrument-style) ═══ */}
        <div id="section-type">
        <div className="field-container">
          {/* Module header row — title left, LED scope right */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-shrink-0">
              <div className="module-header">Font Preset</div>
              <div className="module-desc">Choose a font combination for slides.</div>
            </div>
            <ScopeSelector
              variant="led"
              scope={fontScope}
              onScopeChange={setFontScope}
              uniqueCount={uniqueFonts}
              diversityNoun="fonts"
              confirmMessage={(() => {
                const currentFont = FONT_PRESETS.find(p => p.id === (active.fontPreset ?? DEFAULT_FONT_PRESET));
                return `Slides use ${uniqueFonts} different fonts. Apply "${currentFont?.name || 'current font'}" to all ${slides.length} slides?`;
              })()}
              onConfirmApply={() => {
                const fontId = active.fontPreset ?? DEFAULT_FONT_PRESET;
                const currentFont = FONT_PRESETS.find(p => p.id === fontId);
                onBulkSlideChange(
                  { fontPreset: fontId },
                  `Font changed to ${currentFont?.name || fontId} on all slides`
                );
              }}
            />
          </div>
          <div className="module-rule" />

          {/* 3×3 font preset grid — instrument buttons */}
          <div className="grid grid-cols-3 gap-2">
            {FONT_PRESETS.map((preset) => {
              const isActive = (active.fontPreset ?? DEFAULT_FONT_PRESET) === preset.id;
              const accent = preset.accent || '#ff007f';
              const accentGlow = preset.accentGlow || 'rgba(255,0,127,0.55)';
              return (
                <button
                  key={preset.id}
                  onClick={() => {
                    if (fontScope === 'all') {
                      onBulkSlideChange(
                        { fontPreset: preset.id },
                        `Font changed to ${preset.name} on all slides`
                      );
                    } else {
                      updateActive({ fontPreset: preset.id });
                    }
                  }}
                  className={`font-preset-btn${isActive ? ' font-preset-btn-active' : ''}`}
                >
                  {/* LED dot — top-right corner */}
                  <div
                    className={`font-preset-led${isActive ? ' font-preset-led-active' : ''}`}
                    style={isActive ? {
                      background: accent,
                      boxShadow: `inset 0 1px 1px rgba(255,255,255,0.3), 0 0 4px ${accentGlow}`,
                    } : undefined}
                  />
                  {/* Font name + subtitle preview */}
                  <div className="relative z-2 flex flex-col items-center gap-0.5">
                    <span
                      className={`font-preset-label${isActive ? ' font-preset-label-active' : ''}`}
                      style={{
                        fontFamily: preset.title,
                        fontWeight: preset.titleWeight || 600,
                        ...(isActive ? {
                          color: accent,
                          textShadow: `0 0 6px ${accentGlow}`,
                        } : {}),
                      }}
                    >
                      {preset.name}
                    </span>
                    <span
                      className="text-[10px] truncate"
                      style={{
                        fontFamily: preset.subtitle,
                        color: isActive ? `${accent}88` : '#5a5c5f',
                      }}
                    >
                      {preset.titlePreview}
                    </span>
                  </div>
                  {/* Accent bar — bottom edge */}
                  <div
                    className={`font-preset-bar${isActive ? ' font-preset-bar-active' : ''}`}
                    style={isActive ? {
                      background: accent,
                      boxShadow: `0 -1px 5px ${accentGlow}`,
                    } : undefined}
                  />
                </button>
              );
            })}
          </div>
        </div>
        </div>

        {/* ═══ HEADER section — Title ═══ */}
        <div id="section-header" className="space-y-4">

        {/* Title */}
        <Field label="Title" data-editor-field="title">
          <FormattedTextarea
            value={active.title}
            onValueChange={(val) => updateActive({ title: val })}
            className="textarea-base"
            rows={2}
            placeholder="Main title"
            align={active.titleAlign || 'left'}
            onAlignChange={(a) => updateActive({ titleAlign: a })}
          />
          
          {/* Title Font Size Slider */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="mb-3 module-desc">Adjust the title font size.</div>
            <DarkenSlider
              levels={TITLE_FONT_SIZE_LEVELS}
              value={active.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE}
              onChange={(v) => updateActive({ titleFontSize: v })}
            />
          </div>
        </Field>
        </div>

        {/* ═══ Kicker + Subtitle (tile pair) ═══ */}
        <div className="block-grid">

        {/* Kicker */}
        <div className={kickerSubTileCls.kicker}>
        <div className="field-container" data-editor-field="kicker">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="module-header">Kicker</div>
              <div className="module-desc">Small text above title.</div>
            </div>
            <ToggleSwitch
              checked={kickerEnabled}
              onChange={(checked) => updateActive({ kickerEnabled: checked })}
              size="sm"
            />
          </div>
          {/* Kicker controls — animated collapse */}
          <CollapseTransition open={kickerEnabled}>
            <div className="mt-3">
              <input
                value={active.kicker || ''}
                onChange={(e) => updateActive({ kicker: e.target.value })}
                className="input-base"
                placeholder="e.g. ABOUT / ROADMAP / NEXT"
              />
              <div className="mt-4 pt-4 border-t border-white/10">
                <DarkenSlider
                  levels={KICKER_FONT_SIZE_LEVELS}
                  value={active.kickerFontSize ?? DEFAULT_KICKER_FONT_SIZE}
                  onChange={(v) => updateActive({ kickerFontSize: v })}
                />
              </div>
            </div>
          </CollapseTransition>
        </div>
        </div>

        {/* Subtitle */}
        <div id="section-text" className={kickerSubTileCls.subtitle}>
        <div className="field-container" data-editor-field="subtitle">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="module-header">Subtitle</div>
              <div className="module-desc">Text below title.</div>
            </div>
            <ToggleSwitch
              checked={subtitleEnabled}
              onChange={(checked) => updateActive({ subtitleEnabled: checked })}
              size="sm"
            />
          </div>
          <CollapseTransition open={subtitleEnabled}>
            <div className="mt-3">
              <textarea
                value={active.subtitle || ''}
                onChange={(e) => updateActive({ subtitle: e.target.value })}
                className="textarea-base"
                rows={2}
                placeholder="Short subtitle"
              />
              <div className="mt-4 pt-4 border-t border-white/10">
                <DarkenSlider
                  levels={SUBTITLE_FONT_SIZE_LEVELS}
                  value={active.subtitleFontSize ?? DEFAULT_SUBTITLE_FONT_SIZE}
                  onChange={(v) => updateActive({ subtitleFontSize: v })}
                />
              </div>
            </div>
          </CollapseTransition>
        </div>
        </div>

        </div>

        {/* Body */}
        <div className="field-container" data-editor-field="body">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="module-header">Body</div>
              <div className="module-desc">Main body text.</div>
            </div>
            <ToggleSwitch
              checked={bodyEnabled}
              onChange={(checked) => updateActive({ bodyEnabled: checked })}
              size="sm"
            />
          </div>
          <CollapseTransition open={bodyEnabled}>
            <div className="mt-3">
              <FormattedTextarea
                value={active.body || ''}
                onValueChange={(val) => updateActive({ body: val })}
                className="textarea-base"
                rows={3}
                placeholder="Body text for the slide"
                align={active.bodyAlign || 'left'}
                onAlignChange={(a) => updateActive({ bodyAlign: a })}
              />

              {/* Body Font Size Slider */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="mb-3 module-desc">Adjust the body font size.</div>
                <DarkenSlider
                  levels={BODY_FONT_SIZE_LEVELS}
                  value={active.bodyFontSize ?? DEFAULT_BODY_FONT_SIZE}
                  onChange={(v) => updateActive({ bodyFontSize: v })}
                />
              </div>
            </div>
          </CollapseTransition>
        </div>

        {/* ═══ COLOR section — Color Palette (Session 38: Instrument-style) ═══ */}
        <div id="section-color">
            <div className="field-container">
              {/* Module header row — title left, LED scope right */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-shrink-0">
                  <div className="module-header">Color Palette</div>
                  <div className="module-desc">Choose a color scheme for slides.</div>
                </div>
                <ScopeSelector
                  variant="led"
                  scope={paletteScope}
                  onScopeChange={setPaletteScope}
                  uniqueCount={uniquePalettes}
                  diversityNoun="palettes"
                  confirmMessage={(() => {
                    const pid = active.palette || DEFAULT_PALETTE;
                    const name = PALETTE_PRESETS.find(p => p.id === pid)?.name || 'Custom';
                    return `Slides use ${uniquePalettes} different palettes. Apply "${name}" to all ${slides.length} slides?`;
                  })()}
                  onConfirmApply={() => {
                    const pid = active.palette || DEFAULT_PALETTE;
                    const patch = { palette: pid };
                    if (pid === 'custom' && active.paletteCustom) patch.paletteCustom = active.paletteCustom;
                    const name = PALETTE_PRESETS.find(p => p.id === pid)?.name || 'Custom';
                    onBulkSlideChange(patch, `Palette changed to ${name} on all slides`);
                  }}
                />
              </div>
              <div className="module-rule" />
              <PalettePicker
                value={active.palette}
                customPalette={active.paletteCustom}
                onChange={(patch) => updateActive(patch)}
                slides={slides}
                activeSlide={active}
                scope={paletteScope}
                onScopeChange={setPaletteScope}
                onBulkChange={(patch, hint) => onBulkSlideChange(patch, hint)}
                template={template}
              />
            </div>
        </div>

        {/* ═══ Unified lower grid — Blocks, Widgets, Logo, Footer ═══ */}
        <div className="block-grid">

        {/* Bullets */}
        <div id="section-blocks" className={tileCls.bullets} data-editor-field="bullets">
        <BulletsEditor
          enabled={bulletsEnabled}
          onToggle={(enabled) => updateActive({ bulletsEnabled: enabled })}
          bullets={active.bullets || []}
          onChange={(bullets) => updateActive({ bullets })}
        />
        </div>

        {/* Tags */}
        <div className={tileCls.tags} data-editor-field="tags">
        <TagsEditor
          tags={active.meta || []}
          tagsEnabled={active.tagsEnabled !== false}
          onChange={(meta) => updateActive({ meta })}
          onToggle={(enabled) => updateActive({ tagsEnabled: enabled })}
          slideIndex={activeIndex}
        >
          <div className="mt-4 pt-4 border-t border-white/10">
            <DarkenSlider
              levels={TAGS_FONT_SIZE_LEVELS}
              value={active.tagsFontSize ?? DEFAULT_TAGS_FONT_SIZE}
              onChange={(v) => updateActive({ tagsFontSize: v })}
            />
          </div>
        </TagsEditor>
        </div>

        {/* Widget Editors */}
        <div className={tileCls.progress} data-editor-field="progress">
        <ProgressEditor
          progress={active.progress}
          onChange={(progress) => updateActive({ progress })}
        />
        </div>

        <div className={tileCls.stats} data-editor-field="stats">
        <StatsEditor
          stats={active.stats || []}
          onChange={(stats) => updateActive({ stats })}
        >
          <div className="mt-4 pt-4 border-t border-white/10">
            <DarkenSlider
              levels={STATS_FONT_SIZE_LEVELS}
              value={active.statsFontSize ?? DEFAULT_STATS_FONT_SIZE}
              onChange={(v) => updateActive({ statsFontSize: v })}
            />
          </div>
        </StatsEditor>
        </div>

        <div className={tileCls.callout} data-editor-field="callout">
        <CalloutEditor
          callout={active.callout}
          onChange={(callout) => updateActive({ callout })}
        />
        </div>

        <div className={tileCls.imageWidget} data-editor-field="imageWidget">
        <ImageWidgetEditor
          imageWidget={active.imageWidget}
          onChange={(imageWidget) => updateActive({ imageWidget })}
        />
        </div>

        <div className={tileCls.videoWidget} data-editor-field="videoWidget">
        <VideoWidgetEditor
          videoWidget={active.videoWidget}
          onChange={(videoWidget) => updateActive({ videoWidget })}
          allSlides={slides}
          resolveErrors={resolveErrors}
        />
        </div>

        {/* Logo */}
        <div id="section-logo" className={tileCls.logo}>
        <LogoEditor
          logoEnabled={logoEnabled}
          logoUrl={logoUrl}
          logoAssetId={logoAssetId}
          logoTitle={logoTitle}
          logoSubtitle={logoSubtitle}
          logoSize={logoSize}
          logoIsDark={logoIsDark}
          logoInvert={logoInvert}
          onLogoEnabledChange={(enabled) => onLogoChange({ logo_enabled: enabled })}
          onLogoChange={onLogoChange}
          onLogoTitleChange={(title) => onLogoChange({ logo_title: title })}
          onLogoSubtitleChange={(subtitle) => onLogoChange({ logo_subtitle: subtitle })}
          onLogoSizeChange={(size) => onLogoChange({ logo_size: size })}
          onLogoInvertChange={(invert) => onLogoChange({ logo_invert: invert })}
        />
        </div>

        {/* Content Order — secondary control (primary is preview drag) */}
        <div id="section-content-order" className="block-expanded">
        <ContentOrderEditor
          data={active}
          onChange={(newOrder) => updateActive({ contentOrder: newOrder })}
        />
        </div>

        {/* Footer (CTA) */}
        <div id="section-footer" className={tileCls.footer} data-editor-field="footer">
        <CtaEditor
          cta={active.cta}
          ctaButton={active.ctaButton}
          footerEnabled={footerEnabled}
          onChange={({ cta, ctaButton }) => updateActive({ cta, ctaButton })}
          onToggle={(enabled) => updateActive({ footerEnabled: enabled })}
          slideIndex={activeIndex}
          totalSlides={slides.length}
          slides={slides}
          onBulkSlideChange={onBulkSlideChange}
        />
        </div>

        </div>
      </div>
      </>
      )}
    </div>
  );
}
